'use client';

import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { StyledContentContainer } from '../styles';
import UpdateModal from './UpdateModal';
import { getOfferingUpdateById } from 'services/offerings';
import { trackEvent } from 'utils/analytics';
import useGetLatestUpdate from 'hooks/useGetLatestUpdate';
import useIsMobile from 'hooks/useIsMobile';

const OfferingUpdateHandler = ({ offeringData }) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();

  // Get update ID and private token
  const updateId = params?.slug?.[2];
  const privateUpdateToken = searchParams?.get('private_update_token');
  const isUpdatesUrl = params?.slug?.[1] === 'update';

  // State
  const [offeringUpdateIsOpen, setOfferingUpdateIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [offeringUpdateState, setOfferingUpdateState] = useState({});
  const [extractedUpdateId, setExtractedUpdateId] = useState(null);

  // Get latest update
  const { latestUpdate } = useGetLatestUpdate();

  // Extract update ID from URL path if needed
  useEffect(() => {
    if (privateUpdateToken && !updateId && window?.location?.pathname) {
      const pathParts = window.location.pathname.split('/');
      const updateIndex = pathParts.findIndex((part) => part === 'update');
      if (updateIndex !== -1 && pathParts.length > updateIndex + 1) {
        const urlUpdateId = pathParts[updateIndex + 1];
        if (urlUpdateId) {
          setExtractedUpdateId(urlUpdateId);
        }
      }
    }
  }, [privateUpdateToken, updateId]);

  // Determine which update ID to use
  const effectiveUpdateId = useMemo(() => {
    if (updateId) return updateId;
    if (extractedUpdateId) return extractedUpdateId;
    return latestUpdate?.[0]?.id;
  }, [updateId, extractedUpdateId, latestUpdate]);

  // Fetch update data based on whether we have a private token or not
  const { data: updateData, isFetching: isUpdateFetching } = useQuery({
    queryKey: ['getOfferingUpdate', effectiveUpdateId, privateUpdateToken],
    queryFn: () => getOfferingUpdateById(effectiveUpdateId, privateUpdateToken),
    enabled: Boolean(effectiveUpdateId),
  });

  const stableUpdateId = updateId || extractedUpdateId;
  const isDirectUpdateNavigation = Boolean(
    stableUpdateId && (privateUpdateToken || (updateId && isUpdatesUrl)),
  );

  useEffect(() => {
    if (!isDirectUpdateNavigation || hasOpened) return;
    if (!isUpdateFetching && !updateData) return;
    setOfferingUpdateIsOpen(true);
    setHasOpened(true);
  }, [isDirectUpdateNavigation, hasOpened, isUpdateFetching, updateData]);

  useEffect(() => {
    if (!updateData) return;
    if (!(privateUpdateToken || (updateId && isUpdatesUrl))) return;
    if (!updateData.id || (!updateData.title && !updateData.content)) return;

    trackEvent('offering_update_view', {
      offeringId: offeringData?.id,
      offeringTitle: offeringData?.name,
      updateId: effectiveUpdateId,
      updateTitle: updateData?.title,
      isPrivate: Boolean(privateUpdateToken),
    });

    setOfferingUpdateState(updateData);
  }, [
    updateData,
    privateUpdateToken,
    offeringData?.id,
    offeringData?.name,
    effectiveUpdateId,
    updateId,
    isUpdatesUrl,
  ]);

  // Handle opening modal for latest update when on updates URL
  useEffect(() => {
    if (latestUpdate?.[0]?.id && isUpdatesUrl && !hasOpened && !updateId) {
      trackEvent('offering_update_view', {
        offeringId: offeringData?.id,
        offeringTitle: offeringData?.name,
        updateId: latestUpdate?.[0]?.id,
        updateTitle: latestUpdate?.[0]?.title,
      });

      setOfferingUpdateIsOpen(true);
      setOfferingUpdateState(updateData || {});
      setHasOpened(true);
    }
  }, [
    latestUpdate,
    isUpdatesUrl,
    hasOpened,
    offeringData?.id,
    offeringData?.name,
    privateUpdateToken,
    updateData,
    updateId,
  ]);

  const isLoading = isDirectUpdateNavigation && isUpdateFetching && !offeringUpdateState?.id;

  return (
    <UpdateModal
      isOpen={offeringUpdateIsOpen}
      onClose={() => setOfferingUpdateIsOpen(false)}
      updateData={offeringUpdateState}
      isLoading={isLoading}
      isMobile={isMobile}
      StyledContentContainer={StyledContentContainer}
    />
  );
};

export default OfferingUpdateHandler;
